import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import * as CaNumbersActions from './ca-numbers.actions';
import {
  addCaNumber,
  addCaNumberFailure,
  addCaNumberSuccess,
  deleteCaNumber,
  deleteCaNumberFailure,
  deleteCaNumberSuccess,
  updateCaNumber,
} from './ca-numbers.actions';
import * as CaNumbersSelectors from './ca-numbers.selectors';
import { CaNumbersService } from './ca-numbers.service';

export const loadCaNumbers$ = createEffect(
  (
    actions$ = inject(Actions),
    caNumbersService = inject(CaNumbersService),
    store = inject(Store),
  ) =>
    actions$.pipe(
      ofType(
        CaNumbersActions.loadCaNumbers,
        CaNumbersActions.setPage,
        CaNumbersActions.setSort,
        CaNumbersActions.setSearch,
      ),
      withLatestFrom(
        store.select(CaNumbersSelectors.selectQ),
        store.select(CaNumbersSelectors.selectOffset),
        store.select(CaNumbersSelectors.selectLimit),
        store.select(CaNumbersSelectors.selectSort),
      ),
      switchMap(([action, q, offset, limit, sort]) =>
        caNumbersService.getCaNumbers({ q, offset: offset + 1, limit, sort }).pipe(
          map((response) =>
            CaNumbersActions.loadCaNumbersSuccess({
              caNumbers: response.caNumbers,
              total: response.total,
            }),
          ),
          catchError((error) => of(CaNumbersActions.loadCaNumbersFailure({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const deleteCaNumber$ = createEffect(
  (actions$ = inject(Actions), caNumbersService = inject(CaNumbersService)) =>
    actions$.pipe(
      ofType(deleteCaNumber),
      switchMap(({ id }) =>
        caNumbersService.deleteCaNumber(id).pipe(
          map(() => deleteCaNumberSuccess({ id })),
          catchError((error) => of(deleteCaNumberFailure({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const loadSingleCaNumber$ = createEffect(
  (actions$ = inject(Actions), caNumbersService = inject(CaNumbersService)) =>
    actions$.pipe(
      ofType(CaNumbersActions.loadSingleCaNumber),
      switchMap(({ id }) =>
        caNumbersService.getCaNumberById(id).pipe(
          map((caNumber) => CaNumbersActions.loadSingleCaNumberSuccess({ caNumber })),
          catchError((error) => of(CaNumbersActions.loadSingleCaNumberFailure({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const updateCaNumber$ = createEffect(
  (actions$ = inject(Actions), caNumbersService = inject(CaNumbersService)) =>
    actions$.pipe(
      ofType(updateCaNumber),
      switchMap(({ id, payload }) =>
        caNumbersService.updateCaNumber(id, payload).pipe(
          map((caNumber) => CaNumbersActions.updateCaNumberSuccess({ caNumber })),
          catchError((error) => of(CaNumbersActions.updateCaNumberFailure({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const updateCaNumberSuccess$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) =>
    actions$.pipe(
      ofType(CaNumbersActions.updateCaNumberSuccess),
      map(({ caNumber }) => {
        router.navigate(['/products/ca-numbers', caNumber.id, 'view']);
        return { type: '[CA Numbers] Navigation Complete' };
      }),
    ),
  { functional: true },
);

export const showUpdateErrorSnackbar = createEffect(
  (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(CaNumbersActions.updateCaNumberFailure),
      tap(({ error }) => {
        let message = 'Failed to update CA Number';

        // Check for nested error structure
        let errorsObj = error;
        if (error && error.error && error.error.errors) {
          errorsObj = error.error.errors;
        }

        if (errorsObj && typeof errorsObj === 'object' && !Array.isArray(errorsObj)) {
          message = Object.values(errorsObj).join('\n');
        } else if (typeof error === 'string') {
          message = error;
        }

        snackBar.open(message, 'Close', {
          duration: 6000,
          panelClass: ['snackbar-error'],
          horizontalPosition: 'end',
          verticalPosition: 'bottom',
        });
      }),
    ),
  { functional: true, dispatch: false },
);

export const addCaNumber$ = createEffect(
  (actions$ = inject(Actions), caNumbersService = inject(CaNumbersService)) =>
    actions$.pipe(
      ofType(addCaNumber),
      switchMap(({ payload }) =>
        caNumbersService.addCaNumber(payload).pipe(
          map((caNumber) => addCaNumberSuccess({ caNumber })),
          catchError((error) => of(addCaNumberFailure({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const addCaNumberSuccess$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) =>
    actions$.pipe(
      ofType(addCaNumberSuccess),
      map(({ caNumber }) => {
        router.navigate(['/products/ca-numbers', caNumber.id, 'view']);
        return { type: '[CA Numbers] Navigation Complete' };
      }),
    ),
  { functional: true },
);

export const showAddErrorSnackbar = createEffect(
  (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(addCaNumberFailure),
      tap(({ error }) => {
        let message = 'Failed to add CA Number';

        // Check for nested error structure
        let errorsObj = error;
        if (error && error.error && error.error.errors) {
          errorsObj = error.error.errors;
        }

        if (errorsObj && typeof errorsObj === 'object' && !Array.isArray(errorsObj)) {
          message = Object.values(errorsObj).join('\n');
        } else if (typeof error === 'string') {
          message = error;
        }

        snackBar.open(message, 'Close', {
          duration: 6000,
          panelClass: ['snackbar-error'],
          horizontalPosition: 'end',
          verticalPosition: 'bottom',
        });
      }),
    ),
  { functional: true, dispatch: false },
);

export const addCaFromOrder$ = createEffect(
  (actions$ = inject(Actions), caNumbersService = inject(CaNumbersService)) =>
    actions$.pipe(
      ofType(CaNumbersActions.addCaFromOrder),
      switchMap(({ payload }) =>
        caNumbersService.addCaNumber(payload).pipe(
          map((caNumber) => CaNumbersActions.addCaFromOrderSuccess({ caNumber })),
          catchError((error) => of(CaNumbersActions.addCaFromOrderFailure({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const showAddFromOrderErrorSnackbar = createEffect(
  (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(CaNumbersActions.addCaFromOrderFailure),
      tap(({ error }) => {
        let message = 'Failed to add CA Number';

        // Check for nested error structure
        let errorsObj = error;
        if (error && error.error && error.error.errors) {
          errorsObj = error.error.errors;
        }

        if (errorsObj && typeof errorsObj === 'object' && !Array.isArray(errorsObj)) {
          message = Object.values(errorsObj).join('\n');
        } else if (typeof error === 'string') {
          message = error;
        }

        snackBar.open(message, 'Close', {
          duration: 6000,
          panelClass: ['snackbar-error'],
          horizontalPosition: 'end',
          verticalPosition: 'bottom',
        });
      }),
    ),
  { functional: true, dispatch: false },
);
